import { Layout } from "@/components/layout/layout";
import { useParams, Link } from "wouter";
import {
  useGetVenue,
  useGetVenueStaff,
  useGetVenueUpcomingTournaments,
  getGetVenueQueryKey,
  getGetVenueStaffQueryKey,
  getGetVenueUpcomingTournamentsQueryKey,
} from "@workspace/api-client-react";
import { MapPin, Users, Loader2, ArrowLeft, ExternalLink } from "lucide-react";
import { TournamentCard } from "@/components/tournament-card";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

export default function VenueDetail() {
  const { id } = useParams<{ id: string }>();
  const venueId = Number(id);

  const { data: venue, isLoading: venueLoading } = useGetVenue(venueId, {
    query: { enabled: !!venueId, queryKey: getGetVenueQueryKey(venueId) }
  });

  const { data: staff, isLoading: staffLoading } = useGetVenueStaff(venueId, {
    query: { enabled: !!venueId, queryKey: getGetVenueStaffQueryKey(venueId) }
  });

  const upcomingParams = { limit: 3 };
  const { data: upcoming, isLoading: upcomingLoading } = useGetVenueUpcomingTournaments(
    venueId,
    upcomingParams,
    { query: { enabled: !!venueId, queryKey: getGetVenueUpcomingTournamentsQueryKey(venueId, upcomingParams) } },
  );

  if (venueLoading) return <Layout><div className="flex-1 flex justify-center items-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div></Layout>;
  if (!venue) return <Layout><div className="flex-1 flex justify-center items-center">Venue not found</div></Layout>;

  const fullAddress = [venue.address, venue.city, venue.state].filter(Boolean).join(", ");
  const mapQuery = encodeURIComponent(fullAddress || venue.name);
  const hasCoords = typeof venue.latitude === "number" && typeof venue.longitude === "number";
  let mapEmbedUrl: string;
  let mapLinkUrl: string;
  if (hasCoords) {
    const lat = venue.latitude as number;
    const lon = venue.longitude as number;
    const delta = 0.01;
    const bbox = `${lon - delta},${lat - delta},${lon + delta},${lat + delta}`;
    mapEmbedUrl = `https://www.openstreetmap.org/export/embed.html?bbox=${encodeURIComponent(bbox)}&layer=mapnik&marker=${lat},${lon}`;
    mapLinkUrl = `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lon}#map=16/${lat}/${lon}`;
  } else {
    mapEmbedUrl = `https://www.openstreetmap.org/export/embed.html?layer=mapnik&query=${mapQuery}`;
    mapLinkUrl = `https://www.openstreetmap.org/search?query=${mapQuery}`;
  }

  return (
    <Layout>
      <div className="bg-card border-b border-border pt-8 pb-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <Link href="/venues" className="inline-flex items-center text-sm text-muted-foreground hover:text-primary mb-6">
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to Venues
          </Link>
          <h1 className="text-4xl md:text-5xl font-bold text-foreground tracking-tight mb-4">{venue.name}</h1>
          <div className="flex flex-wrap gap-4 text-muted-foreground">
            <div className="flex items-center">
              <MapPin className="mr-2 h-5 w-5 text-primary" />
              {fullAddress || "Address TBA"}
            </div>
            {venue.capacity && (
              <div className="flex items-center">
                <Users className="mr-2 h-5 w-5 text-primary" />
                {venue.capacity} Player Capacity
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            <Card className="bg-background border-border overflow-hidden">
              <div className="aspect-[16/9] w-full bg-muted">
                <iframe
                  title={`Map of ${venue.name}`}
                  src={mapEmbedUrl}
                  className="w-full h-full border-0"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
              <CardContent className="p-4 flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Map data &copy; OpenStreetMap contributors</span>
                <a
                  href={mapLinkUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center text-primary hover:underline"
                >
                  Open in OpenStreetMap <ExternalLink className="ml-1 h-3.5 w-3.5" />
                </a>
              </CardContent>
            </Card>

            <div>
              <h2 className="text-2xl font-bold mb-6">Next 3 Tournaments Here</h2>
              {upcomingLoading ? (
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              ) : !upcoming || upcoming.length === 0 ? (
                <p className="text-muted-foreground bg-card p-6 rounded-xl border border-border">No upcoming tournaments scheduled here yet.</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {upcoming.map(t => (
                    <TournamentCard key={t.id} tournament={t} />
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="space-y-8">
            <Card className="bg-background border-border">
              <CardContent className="p-6">
                <h3 className="font-bold text-lg mb-4">Getting Here & Parking</h3>
                <p className="text-muted-foreground whitespace-pre-wrap">
                  {venue.parkingNotes || venue.notes || "No specific parking or arrival notes for this venue yet. Check the map and give the venue a call if you need directions."}
                </p>
              </CardContent>
            </Card>

            <Card className="bg-background border-border">
              <CardContent className="p-6">
                <h3 className="font-bold text-lg mb-4">Regular Staff</h3>
                {staffLoading ? (
                  <Loader2 className="h-5 w-5 animate-spin text-primary" />
                ) : staff?.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No regular staff assigned.</p>
                ) : (
                  <div className="space-y-4">
                    {staff?.map(member => (
                      <div key={member.id} className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="h-8 w-8 rounded-full bg-secondary/20 flex items-center justify-center text-secondary font-bold text-sm">
                            {member.name.charAt(0)}
                          </div>
                          <span className="font-medium">{member.name}</span>
                        </div>
                        <Badge variant="outline" className="capitalize">{member.role.replace("_", " ")}</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
}
